import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import {
  customerSearchColumns,
  diaChiSearchColumns,
  phuongThucGiaoHangSearchColumns,
  phuongThucVanChuyenSearchColumns,
  QUERY_KEYS,
  taiKhoanNganHangSearchColumns
} from '@/constants';
import { DiaChi, KhachHang, PhuongThucGiaoHang, PhuongTienVanChuyen, TaiKhoanNganHang } from '@/types/schemas';
import { FormFieldV2, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { useHoaDonBanHangSearchContext } from '../../context';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface OtherTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export function OtherTab({ formMode, formState: { state, actions } }: OtherTabProps) {
  const { transactionOptions } = useHoaDonBanHangSearchContext();
  const { watch, setValue } = useFormContext();
  const maGD = watch('ma_gd');

  useEffect(() => {
    if (maGD || transactionOptions.length === 0) return;

    setValue('ma_gd', transactionOptions[0]?.value, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true
    });
  }, [maGD, setValue, transactionOptions]);

  const rowClassName = 'flex items-center gap-2 xl:gap-3';
  const labelClassName = 'w-32 shrink-0 text-[13px] xl:w-40';

  return (
    <div className='w-full overflow-x-auto'>
      <div className='w-full min-w-[1180px] space-y-2 p-3 xl:min-w-0 xl:p-4'>
        <div className={rowClassName}>
          <Label className={labelClassName}>Giao dịch</Label>
          <FormFieldV2
            name='ma_gd'
            className='w-48'
            type='select'
            disabled={formMode === 'view'}
            options={transactionOptions}
          />
        </div>

        <div className={rowClassName}>
          <Label className={labelClassName}>Mã địa chỉ</Label>
          <SearchField<DiaChi>
            type='text'
            value={state.diaChi?.ma_dc || ''}
            searchEndpoint={`/${QUERY_KEYS.DIA_CHI}/`}
            searchColumns={diaChiSearchColumns}
            columnDisplay='ma_dc'
            displayRelatedField='ten_dc'
            dialogTitle='Danh mục địa chỉ giao hàng'
            onRowSelection={actions.setDiaChi}
            disabled={formMode === 'view'}
            className='w-100'
          />
        </div>

        <div className={rowClassName}>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Phương tiện v/c</Label>
            <SearchField<PhuongTienVanChuyen>
              type='text'
              value={state.phuongTienVanChuyen?.ma_ptvc || ''}
              searchEndpoint={`/${QUERY_KEYS.PHUONG_TIEN_VAN_CHUYEN}/`}
              searchColumns={phuongThucVanChuyenSearchColumns}
              columnDisplay='ma_ptvc'
              displayRelatedField='ten_ptvc'
              dialogTitle='Danh mục phương tiện vận chuyển'
              onRowSelection={actions.setPhuongTienVanChuyen}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>

          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Ph/th giao hàng</Label>
            <SearchField<PhuongThucGiaoHang>
              type='text'
              value={state.phuongTienGiaoHang?.ma_ptgh || ''}
              searchEndpoint={`/${QUERY_KEYS.PHUONG_TIEN_GIAO_HANG}/`}
              searchColumns={phuongThucGiaoHangSearchColumns}
              columnDisplay='ma_ptgh'
              displayRelatedField='ten_ptgh'
              dialogTitle='Danh mục phương thức giao hàng'
              onRowSelection={actions.setPhuongTienGiaoHang}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>
        </div>

        <div className={rowClassName}>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Ngân hàng th.toán</Label>
            <SearchField<TaiKhoanNganHang>
              type='text'
              value={state.nganHang?.tknh || ''}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`}
              searchColumns={taiKhoanNganHangSearchColumns}
              columnDisplay='tknh'
              dialogTitle='Danh mục tài khoản ngân hàng'
              onRowSelection={actions.setNganHang}
              disabled={formMode === 'view'}
              className='w-48'
            />
            <div className='w-[415px]'>
              <FormFieldV2
                placeholder='Diễn giải thông tin thanh toán (50 ký tự)'
                name='dien_giai_nh'
                type='text'
                disabled={formMode === 'view'}
                className='w-full'
              />
            </div>
          </div>
        </div>

        <div className={rowClassName}>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Nhóm sản phẩm</Label>
            <div className='w-[200px]'>
              <FormFieldV2 name='ten_vt_thue' disabled={formMode === 'view'} type='text' className='w-full' />
            </div>
          </div>

          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Cục thuế</Label>
            <SearchField<KhachHang>
              type='text'
              value={state.cucThue?.customer_code || ''}
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={customerSearchColumns}
              columnDisplay='customer_code'
              displayRelatedField='customer_name'
              dialogTitle='Danh mục khách hàng'
              classNameRelatedField='w-full'
              onRowSelection={actions.setCucThue}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>
        </div>

        <div className={rowClassName}>
          <Label className={labelClassName}>Lý do hủy</Label>
          <div className='min-w-0 flex-1'>
            <FormFieldV2 name='ly_do_huy' type='text' disabled={formMode === 'view'} placeholder='Nhập lý do hủy đơn' />
          </div>
        </div>

        <div className={rowClassName}>
          <Label className={labelClassName}>Lý do điều chỉnh</Label>
          <div className='min-w-0 flex-1'>
            <FormFieldV2
              name='ly_do'
              placeholder='Nhập lý do thay thế hoặc điều chỉnh hóa đơn điện tử'
              type='text'
              disabled={formMode === 'view'}
            />
          </div>
        </div>

        <div className={rowClassName}>
          <div className='flex items-center gap-3'>
            <Label className={labelClassName}>Số bảng kê</Label>
            <div className='w-[235px]'>
              <FormFieldV2 name='so_bk' type='text' disabled={formMode === 'view'} className='w-full' />
            </div>
          </div>

          <div className='ml-7 flex items-center gap-3'>
            <Label className={labelClassName}>Ngày bảng kê</Label>
            <FormFieldV2 name='ngay_bk' type='date' disabled={formMode === 'view'} className='w-44' />
          </div>
        </div>

        <div className={rowClassName}>
          <Label className={labelClassName}>Ghi chú</Label>
          <div className='min-w-0 flex-1'>
            <FormFieldV2 name='ghi_chu' placeholder='Nhập ghi chú' type='text' disabled={formMode === 'view'} />
          </div>
        </div>
      </div>
    </div>
  );
}
